import GenericTable from "@/components/GenericTable";
import Footer from "@/components/Footer";
import { Tooltip } from "@/components/ui/tooltip";
import { ALL_PAYOUTS_TABLE_COLUMNS } from "@/config/tables";
import { statusMessagesMapping } from "@/constants/paymentStatusMessages";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import {
  Badge,
  Box,
  HStack,
  IconButton,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react";
import { CircleCheck, CircleX, Download } from "lucide-react";
import { useDownloadPayoutReceipt } from "../hooks/useDownloadPayoutReceipt";
import { useFetchAllPayouts } from "../hooks/useFetchAllPayouts";
import { getPayoutChannelName } from "../mappers/banks.mapper";
import type { AllPayoutsFiltersType } from "../types/payouts.type";
import AllPayoutsFilters from "./AllPayoutsFilters";

const TABLE_KEY = "all-payouts";

export default function AllPayoutsTable() {
  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<AllPayoutsFiltersType>(TABLE_KEY);

  const { payouts, nextCursor, isLoading } = useFetchAllPayouts(
    queryFilters,
    cursor
  );

  const { downloadReceipt, downloadingIds } = useDownloadPayoutReceipt();

  const columns = [
    { key: "createdAt", label: "Fecha" },
    { key: "batchId", label: "Lote" },
    { key: "customerName", label: "Nombre" },
    { key: "customerDocumentNumber", label: "Documento" },
    { key: "amount", label: "Monto" },
    { key: "bank", label: "Banco" },
    { key: "status", label: "Estado" },
    { key: "sent", label: "Envío" },
    { key: "actions", label: "" },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
    }).format(new Date(dateString));
  };

  const formatDateWithTime = (dateString: string | undefined) => {
    if (!dateString) return "-";
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Bogota",
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: "Pendiente", color: "gray" },
      processing: { label: "Procesando", color: "blue" },
      completed: { label: "Completado", color: "green" },
      cancelled: { label: "Cancelado", color: "orange" },
      declined: { label: "Rechazado", color: "red" },
    } as const;

    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      color: "gray",
    };

    return (
      <Badge colorPalette={config.color} variant="subtle">
        {config.label}
      </Badge>
    );
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH={0}>
      <AllPayoutsFilters
        queryFilters={queryFilters}
        setQueryFilters={setQueryFilters}
      />
      <GenericTable isLoading={isLoading} columns={columns} fillHeight>
        {payouts.map((payout) => (
          <Table.Row key={payout.id}>
            <Table.Cell color="gray.600">
              {formatDate(payout.createdAt)}
            </Table.Cell>
            <Table.Cell>
              <Text>{payout.batchId}</Text>
            </Table.Cell>
            <Table.Cell color="gray.800">{payout.customerName}</Table.Cell>
            <Table.Cell color="gray.600">
              {payout.customerDocumentNumber}
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(payout.amount)}
              </Text>
            </Table.Cell>
            <Table.Cell color="gray.600">
              {getPayoutChannelName(payout)}
            </Table.Cell>
            <Table.Cell>
              {payout.statusMessage ? (
                <Tooltip
                  content={
                    <Box p={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        {statusMessagesMapping[payout.statusMessage]}
                      </Text>
                    </Box>
                  }
                >
                  {getStatusBadge(payout.status)}
                </Tooltip>
              ) : (
                getStatusBadge(payout.status)
              )}
            </Table.Cell>
            <Table.Cell>
              {payout.ownerNotifiedAt ? (
                <Tooltip
                  content={
                    <Box p={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        Notificación enviada
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatDateWithTime(payout.ownerNotifiedAt)}
                      </Text>
                      <Text fontSize="xs" mt={1}>
                        {payout.customerEmail}
                      </Text>
                    </Box>
                  }
                >
                  <Box as="span" display="inline-flex" cursor="help">
                    <CircleCheck color="#3CB18B" size={20} strokeWidth={2} />
                  </Box>
                </Tooltip>
              ) : (
                <CircleX color="#EC5858" size={20} strokeWidth={2} />
              )}
            </Table.Cell>
            <Table.Cell>
              <HStack gap={2}>
                <IconButton
                  aria-label="Descargar comprobante"
                  variant="ghost"
                  size="xs"
                  onClick={() =>
                    downloadReceipt(payout.id, !!payout.receiptGenerated)
                  }
                  disabled={downloadingIds.has(payout.id)}
                >
                  {downloadingIds.has(payout.id) ? (
                    <Spinner size="sm" />
                  ) : (
                    <Download size="1.2rem" />
                  )}
                </IconButton>
              </HStack>
            </Table.Cell>
          </Table.Row>
        ))}
      </GenericTable>

      <Box mt="auto" pt={6} flexShrink={0}>
        <Footer
          tableProps={{
            fileName: "detalle_dispersiones",
            items: payouts,
            columns: ALL_PAYOUTS_TABLE_COLUMNS,
            pagination: {
              tableKey: TABLE_KEY,
              nextCursor,
              setCursor,
              cursor,
            },
          }}
        />
      </Box>
    </Box>
  );
}
